import { useMemo, useState } from 'react';
import { sortHand, type BhabhiLegal, type BhabhiView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };

export function BhabhiTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as BhabhiView;
  const legal = useGame((s) => s.legal) as BhabhiLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.cards ?? []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const ended = view.phase === 'MATCH_END';
  const last = view.lastTrick;
  const collecting = view.phase === 'TRICK_END' && last;

  const onCard = (c: string) => {
    if (!myTurn || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const hint = !myTurn
    ? null
    : view.pile.length === 0
      ? view.trickNo === 1
        ? t('bh.firstHint')
        : t('bh.leadHint')
      : legal?.thulla
        ? t('bh.dumpHint', { suit: SUIT_SYMBOL[view.leadSuit!] ?? '' })
        : t('bh.followHint', { suit: SUIT_SYMBOL[view.leadSuit!] ?? '' });

  const roundNote = collecting
    ? last!.outcome === 'pickup'
      ? t('bh.pickup', { name: name(last!.taker), n: last!.plays.length })
      : t('bh.discard', { name: name(last!.taker) })
    : null;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => {
          const place = view.out.indexOf(s);
          return (
            <SeatView
              key={s}
              seat={s}
              info={seats[s]}
              mySeat={mySeat}
              total={4}
              turn={turn}
              speech={speech[s]}
              folded={place >= 0}
              sub={<span className="dim num">{t('bh.cards', { n: view.handCounts[s] ?? 0 })}</span>}
              tags={
                <>
                  {place >= 0 && <span className="tag seen">{t('bh.out', { n: place + 1 })}</span>}
                  {ended && view.bhabhi === s && <span className="tag blind">{t('bh.bhabhiTag')}</span>}
                  {!ended && place < 0 && (view.voids[s] ?? []).length > 0 && s !== mySeat && (
                    <span className="tag" title={t('bh.voids')}>
                      ✕ {(view.voids[s] ?? []).map((x) => SUIT_SYMBOL[x]).join('')}
                    </span>
                  )}
                </>
              }
            />
          );
        })}
        <div className="felt-center centered">
          <div className="center-note">
            {t('bh.round', { n: view.trickNo })} · {t('bh.discarded', { n: view.discarded })}
            {view.leadSuit && !collecting && ` · ${t('bh.lead')} ${SUIT_SYMBOL[view.leadSuit]}`}
          </div>
          <div className={`trick ${collecting ? 'collect' : ''}`} key={`trick-${view.trickNo}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.pile.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = collecting ? OFFSETS[seatPosition(last!.taker, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {roundNote && (
            <div className={`pill ${last!.outcome === 'pickup' ? 'danger' : 'accent'}`} role="status">
              {last!.outcome === 'pickup' && last!.thulla !== null ? `${t('bh.thulla')} ` : ''}
              {roundNote}
            </div>
          )}
          {ended && (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{view.bhabhi === null ? t('bh.noBhabhi') : t('bh.isBhabhi', { name: name(view.bhabhi) })}</strong>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('bh.cards', { n: view.handCounts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {hint && (
          <div className="action-bar">
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>{hint}</span>
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={view.trickNo === 1 && view.pile.length === 0 ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('bh.player')}</th>
              <th>{t('bh.cardsLeft')}</th>
            </tr>
          </thead>
          <tbody>
            {[...view.out, ...[0, 1, 2, 3].filter((s) => !view.out.includes(s)).sort((a, b) => (view.handCounts[a] ?? 0) - (view.handCounts[b] ?? 0))].map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                <td className="num">{view.out.includes(s) ? t('bh.outShort') : (view.handCounts[s] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('bh.scoreHint')}</p>
      </Modal>
    </>
  );
}
